import React, { useEffect, useState } from "react";
import { Users, ShieldCheck, DollarSign, UserPlus, Pencil, Trash2, KeyRound, RefreshCw, Search, Database, Copy, Save, Trash, Lock, BellRing, PhoneCall, Settings } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatCard, Tabs, Modal, Select, toast, Avatar, Spinner, Pagination, PageLoader } from "../components/ui";
import { ROLE_META, money } from "../lib/constants";
import { format } from "date-fns";
import PushToggle from "../components/PushToggle";
import TelephonyTab from "../components/TelephonyTab";
import DuplicatesTab from "../components/DuplicatesTab";
import { tokenStore } from "../lib/api";

const ROLES = [{ value: "agent", label: "Agent" }, { value: "team_leader", label: "Team Leader" }, { value: "admin", label: "Admin" }];

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("users");
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, [tab]);
  if (!stats) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900 dark:text-white flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-indigo-500" /> Admin Panel</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Manage users, deposits, and system settings</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Total Users" value={stats.total_users} icon={Users} testid="ap-users" />
        <StatCard label="Admins" value={stats.admins} icon={ShieldCheck} accent="text-amber-600 dark:text-amber-400" testid="ap-admins" />
        <StatCard label="Total Deposits" value={money(stats.total_deposits)} icon={DollarSign} accent="text-emerald-600 dark:text-emerald-400" testid="ap-deposits" />
        <StatCard label="Total Leads" value={stats.total_leads.toLocaleString()} accent="text-blue-600 dark:text-blue-400" testid="ap-leads" />
        <StatCard label="Total Clients" value={stats.total_clients.toLocaleString()} accent="text-cyan-600 dark:text-cyan-400" testid="ap-clients" />
      </div>

      <Tabs active={tab} onChange={setTab} tabs={[
        { value: "users", label: "Users", icon: Users },
        { value: "deposits", label: "Deposits", icon: DollarSign },
        { value: "passwords", label: "Passwords", icon: Lock },
        { value: "notifications", label: "Notifications", icon: BellRing },
        { value: "telephony", label: "Telephony", icon: PhoneCall },
        { value: "duplicates", label: "Duplicates", icon: Copy },
        { value: "settings", label: "Settings", icon: Settings },
      ]} />

      {tab === "users" && <UsersTab />}
      {tab === "deposits" && <DepositsTab />}
      {tab === "passwords" && <PasswordsTab />}
      {tab === "telephony" && <TelephonyTab />}
      {tab === "duplicates" && <DuplicatesTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "notifications" && (
        <div className="max-w-xl">
          <PushToggle token={tokenStore.get()} />
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const { user } = useAuth();
  const [data, setData] = useState({ users: [] });
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [pw, setPw] = useState(null);
  const [add, setAdd] = useState(false);
  const load = () => api.get("/users").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const del = async (u) => { if (!window.confirm(`Delete ${u.full_name}?`)) return; await api.delete(`/users/${u.id}`); toast("Deleted"); load(); };
  const filtered = data.users.filter((u) => !q || u.full_name.toLowerCase().includes(q.toLowerCase()) || u.username.includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input className="input pl-9" placeholder="Search users..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="ap-user-search" />
        </div>
        <button className="btn-primary" onClick={() => setAdd(true)} data-testid="ap-add-user"><UserPlus className="w-4 h-4" /> Add User</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {filtered.map((u) => (
          <div key={u.id} className="card p-5" data-testid={`ap-user-${u.username}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Avatar name={u.full_name} />
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 dark:text-white truncate">{u.full_name}{u.id === user.id && <span className="text-xs text-slate-400"> (You)</span>}</div>
                  <div className="text-xs text-slate-400 truncate">@{u.username}</div>
                </div>
              </div>
              <span className={`badge ${ROLE_META[u.role]?.cls || ROLE_META.agent.cls}`}>{ROLE_META[u.role]?.label || u.role}</span>
            </div>
            <div className="grid grid-cols-4 gap-1 mt-4 text-center">
              {[["Leads", u.leads.toLocaleString()], ["Clients", u.clients], ["Deposits", money(u.deposits)], ["Converts", u.conversions]].map(([l, v]) => (
                <div key={l}><div className="text-sm font-bold text-slate-800 dark:text-slate-100">{v}</div><div className="text-[10px] uppercase text-slate-400">{l}</div></div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button className="btn-secondary flex-1 py-1.5" onClick={() => setEdit(u)} data-testid={`ap-edit-${u.username}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button className="btn-secondary px-2.5 py-1.5" onClick={() => setPw(u)} title="Reset password"><KeyRound className="w-4 h-4" /></button>
              {u.id !== user.id && <button className="btn-secondary px-2.5 py-1.5 text-rose-500" onClick={() => del(u)}><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
      </div>
      {add && <UserModal title="Add User" onClose={() => setAdd(false)} onDone={() => { setAdd(false); load(); }} />}
      {edit && <UserModal title="Edit User" user={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
      {pw && <ResetPwModal user={pw} onClose={() => setPw(null)} />}
    </div>
  );
}

function UserModal({ title, user, onClose, onDone }) {
  const editing = !!user;
  const [form, setForm] = useState({ username: user?.username || "", full_name: user?.full_name || "", role: user?.role || "agent", password: "", email: user?.email || "" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/users/${user.id}`, { full_name: form.full_name, role: form.role, email: form.email });
      else { if (!form.username || !form.full_name || !form.password) { setBusy(false); return toast("Fill all required fields", "error"); } await api.post("/users", form); }
      toast("Saved"); onDone();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={title} testid="ap-user-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy} data-testid="ap-user-save">Save</button></>}>
      {!editing && <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Username</label><input className="input mt-1" value={form.username} onChange={(e) => upd("username", e.target.value)} data-testid="ap-username" /></div>}
      <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Full name</label><input className="input mt-1" value={form.full_name} onChange={(e) => upd("full_name", e.target.value)} data-testid="ap-fullname" /></div>
      <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Role</label><Select value={form.role} onChange={(v) => upd("role", v)} options={ROLES} testid="ap-role" /></div>
      {!editing && <div><label className="text-sm font-medium text-slate-600 dark:text-slate-300">Password</label><input className="input mt-1" value={form.password} onChange={(e) => upd("password", e.target.value)} data-testid="ap-password" /></div>}
    </Modal>
  );
}

function ResetPwModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); try { await api.put(`/users/${user.id}/password`, { new_password: pw }); toast("Password reset"); onClose(); } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); } };
  return (
    <Modal open onClose={onClose} title={`Reset password — ${user.full_name}`} testid="ap-reset-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy || !pw} data-testid="ap-reset-submit">Reset</button></>}>
      <input className="input" type="text" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="ap-reset-input" />
    </Modal>
  );
}

function DepositsTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 50;
  useEffect(() => {
    setLoading(true);
    api.get("/admin/deposits", { params: { page, page_size: pageSize } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [page]);
  const del = async (id) => { if (!window.confirm("Delete this deposit? Client balance will be reduced.")) return; await api.delete(`/admin/deposits/${id}`); toast("Deposit deleted"); setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id) })); };
  const items = q ? data.items.filter((x) => (x.client_name + x.agent_name + x.amount).toLowerCase().includes(q.toLowerCase())) : data.items;
  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input className="input pl-9" placeholder="Search deposits by client, agent, or amount..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="ap-dep-search" />
      </div>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ap-deposits-table">
            <thead className="bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <tr><th className="p-3 text-left">Client</th><th className="p-3 text-left">Agent</th><th className="p-3 text-left">Amount</th><th className="p-3 text-left">Date</th><th className="p-3 text-left">Notes</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-10 text-center"><Spinner className="w-5 h-5 inline text-slate-400" /></td></tr> :
                items.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 dark:border-slate-800/50">
                    <td className="p-3 font-medium text-slate-800 dark:text-slate-100">{d.client_name}</td>
                    <td className="p-3 text-slate-600 dark:text-slate-300">{d.agent_name}</td>
                    <td className="p-3 font-semibold text-emerald-600 dark:text-emerald-400">{money(d.amount)}</td>
                    <td className="p-3 text-slate-500">{d.created_at ? format(new Date(d.created_at), "MMM d, yyyy") : ""}</td>
                    <td className="p-3 text-slate-500">{d.notes || "-"}</td>
                    <td className="p-3 text-right"><button className="btn-ghost p-1.5 text-rose-500" onClick={() => del(d.id)} data-testid={`ap-dep-del-${d.id}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 border-t border-slate-100 dark:border-slate-800"><Pagination page={page} pageSize={pageSize} total={data.total} onPage={setPage} /></div>
      </div>
    </div>
  );
}

function PasswordsTab() {
  const [opts, setOpts] = useState({ length: 16, upper: true, lower: true, numbers: true, symbols: true });
  const [pw, setPw] = useState("");
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const load = () => api.get("/admin/passwords").then((r) => setHistory(r.data));
  useEffect(() => { load(); }, []);
  const generate = () => {
    let chars = "";
    if (opts.upper) chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (opts.lower) chars += "abcdefghijklmnopqrstuvwxyz";
    if (opts.numbers) chars += "0123456789";
    if (opts.symbols) chars += "!@#$%^&*()_+-=[]{}|;:,.<>?";
    if (!chars) return toast("Select at least one character set", "error");
    let out = "";
    const arr = new Uint32Array(opts.length);
    crypto.getRandomValues(arr);
    for (let i = 0; i < opts.length; i++) out += chars[arr[i] % chars.length];
    setPw(out);
  };
  const save = async () => { if (!pw) return toast("Generate a password first", "error"); await api.post("/admin/passwords", { password: pw, note }); toast("Saved to history"); setNote(""); load(); };
  const clearAll = async () => { if (!window.confirm("Clear all password history?")) return; await api.delete("/admin/passwords"); load(); };
  const Toggle = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-600 dark:text-slate-300">
      <input type="checkbox" checked={opts[k]} onChange={(e) => setOpts((o) => ({ ...o, [k]: e.target.checked }))} data-testid={`pw-${k}`} /> {label}
    </label>
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card p-6">
        <h3 className="font-semibold font-display text-slate-900 dark:text-white flex items-center gap-2 mb-4"><Lock className="w-4 h-4 text-indigo-500" /> Password Generator</h3>
        <label className="text-xs uppercase tracking-wide text-slate-400">Generated Password</label>
        <div className="flex gap-2 mt-1 mb-4">
          <input className="input font-mono" readOnly value={pw} placeholder="Click generate to create a password" data-testid="pw-output" />
          <button className="btn-secondary" onClick={() => { navigator.clipboard.writeText(pw); toast("Copied"); }} disabled={!pw}><Copy className="w-4 h-4" /></button>
          <button className="btn-primary" onClick={generate} data-testid="pw-generate">Generate</button>
        </div>
        <label className="text-xs uppercase tracking-wide text-slate-400">Length: {opts.length}</label>
        <input type="range" min={8} max={32} value={opts.length} onChange={(e) => setOpts((o) => ({ ...o, length: Number(e.target.value) }))} className="w-full mt-1 mb-4 accent-indigo-600" data-testid="pw-length" />
        <div className="grid grid-cols-2 gap-2 mb-4">
          <Toggle k="upper" label="Uppercase (A-Z)" /><Toggle k="lower" label="Lowercase (a-z)" />
          <Toggle k="numbers" label="Numbers (0-9)" /><Toggle k="symbols" label="Symbols (!@#$...)" />
        </div>
        <input className="input mb-3" placeholder="Add Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="pw-note" />
        <button className="btn-primary w-full" onClick={save} data-testid="pw-save"><Save className="w-4 h-4" /> Save to History</button>
      </div>
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold font-display text-slate-900 dark:text-white">Password History ({history.length})</h3>
          {history.length > 0 && <button className="btn-ghost text-rose-500 text-sm" onClick={clearAll} data-testid="pw-clear"><Trash className="w-4 h-4" /> Clear All</button>}
        </div>
        <div className="space-y-2 max-h-[420px] overflow-auto">
          {history.length === 0 && <div className="text-center text-sm text-slate-400 py-8">No saved passwords</div>}
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2.5">
              <div className="min-w-0">
                <div className="font-mono text-sm text-slate-800 dark:text-slate-100 truncate">{h.password}</div>
                <div className="text-xs text-slate-400">{h.note || h.created_by} • {h.created_at ? format(new Date(h.created_at), "MMM d, yyyy HH:mm") : ""}</div>
              </div>
              <button className="btn-ghost p-1.5" onClick={() => { navigator.clipboard.writeText(h.password); toast("Copied"); }}><Copy className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [allowAll, setAllowAll] = useState(false);
  const [granted, setGranted] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([api.get("/settings/app"), api.get("/users")])
      .then(([s, u]) => { setAllowAll(!!s.data.allow_agent_upload); setGranted(s.data.upload_agent_ids || []); setUsers(u.data.users || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const eligible = users.filter((u) => u.role === "agent" || u.role === "team_leader");
  const save = async (patch) => {
    await api.put("/settings/app", patch);
    toast("Settings saved");
  };
  const toggleAll = async (checked) => {
    setAllowAll(checked);
    try { await save({ allow_agent_upload: checked }); }
    catch (e) { setAllowAll(!checked); toast(e.response?.data?.detail || "Failed to save settings", "error"); }
  };
  const toggleAgent = async (id, checked) => {
    const next = checked ? [...granted, id] : granted.filter((x) => x !== id);
    setGranted(next);
    try { await save({ upload_agent_ids: next }); }
    catch (e) { setGranted(granted); toast(e.response?.data?.detail || "Failed to save settings", "error"); }
  };
  return (
    <div className="max-w-xl space-y-4">
      <div className="card p-6">
        <h3 className="font-semibold font-display text-slate-900 dark:text-white flex items-center gap-2 mb-4"><Settings className="w-4 h-4 text-indigo-500" /> Upload Data Access</h3>
        <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-600 dark:text-slate-300">
          <input type="checkbox" checked={allowAll} onChange={(e) => toggleAll(e.target.checked)} data-testid="agent-upload-toggle" /> Allow all agents to use Upload Data
        </label>
        <p className="text-xs text-slate-400 mt-1.5">When enabled, every agent and team leader can open the Upload Data page and import clients via CSV or Excel. Otherwise you can grant access per agent below.</p>
      </div>
      <div className="card p-6">
        <h3 className="font-semibold font-display text-slate-900 dark:text-white flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-indigo-500" /> Per-agent Access</h3>
        <p className="text-xs text-slate-400 mb-3">Admins always have upload access. Tick an agent to let them use Upload Data individually.</p>
        {loading ? <div className="py-6 text-center"><Spinner className="w-5 h-5 inline text-slate-400" /></div> : eligible.length === 0 ? (
          <div className="text-center text-sm text-slate-400 py-6">No agents yet</div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {eligible.map((u) => {
              const on = allowAll || granted.includes(u.id);
              return (
                <label key={u.id} className={`flex items-center justify-between py-2.5 ${allowAll ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{u.full_name}</div>
                    <div className="text-xs text-slate-400">@{u.username} • {ROLE_META[u.role]?.label || u.role}</div>
                  </div>
                  <input type="checkbox" checked={on} disabled={allowAll} onChange={(e) => toggleAgent(u.id, e.target.checked)} data-testid={`ap-upload-${u.username}`} />
                </label>
              );
            })}
          </div>
        )}
        {allowAll && !loading && <p className="text-xs text-slate-400 mt-3">“Allow all agents” is on, so everyone can upload — individual settings are inactive until you switch it off.</p>}
      </div>
    </div>
  );
}
